import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: ${(p) => p.padding || "0"};
  margin: ${(p) => p.margin || "0"};
`;

const MediaWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
`;

const CampaignCarousel = React.memo(({ campaigns, padding, margin, scheduleViewsMap }) => {
  const [flatItems, setFlatItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  // tick always increments on every advance so the playback effect always
  // re-runs, even when idx wraps back to a value it had before.
  const [tick, setTick] = useState(0);

  const timerRef = useRef(null);
  // Mirror of flatItems kept in a ref so the stable `advance` callback
  // can read the current length without being recreated on every render.
  const flatItemsRef = useRef([]);

  // Build the flat item list whenever campaigns change
  useEffect(() => {
    if (!campaigns?.length) {
      flatItemsRef.current = [];
      setFlatItems([]);
      setIsLoading(false);
      return;
    }

    const items = campaigns.flatMap((c) =>
      (c.media || [])
        .filter((m) => m.url)
        .map((m) => ({
          url: m.url,
          mediaType: m.mediaType || "image",
          duration: (c.durationInSeconds || 5) * 1000,
          scheduleId: String(c.scheduleId),
        }))
    );

    flatItemsRef.current = items;
    setFlatItems(items);
    setIdx(0);
    // Increment (not reset) so the playback effect fires even if idx was
    // already 0 before this campaigns update.
    setTick((t) => t + 1);
    setIsLoading(false);
  }, [campaigns]);

  // advance has NO deps — it reads length from the ref, so it is created
  // exactly once and never goes stale. This prevents the playback effect
  // from re-running spuriously when advance changes reference.
  const advance = useCallback(() => {
    const len = flatItemsRef.current.length;
    if (!len) return;
    setIdx((prev) => (prev + 1) % len);
    setTick((t) => t + 1);
  }, []);

  // Playback logic — only depends on idx and tick (both primitives).
  // Images: schedule a setTimeout.
  // Videos: rely on onEnded/onError in the JSX below.
  useEffect(() => {
    if (isLoading || !flatItems.length) return;

    const item = flatItems[idx];
    if (!item) return;

    // Track campaign views (skip synthetic building-media entries)
    if (scheduleViewsMap?.current && item.scheduleId !== "__building_media__") {
      scheduleViewsMap.current[item.scheduleId] =
        (scheduleViewsMap.current[item.scheduleId] || 0) + 1;
    }

    if (item.mediaType !== "image") return; // videos handled by onEnded

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(advance, item.duration);

    return () => {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, tick]); // flatItems/advance read from closure — stable, no need in deps

  if (isLoading) {
    return (
      <Container padding={padding} margin={margin}>
        <div>טוען...</div>
      </Container>
    );
  }

  if (!flatItems.length) {
    return (
      <Container padding={padding} margin={margin}>
        <div>אין תוכן להצגה</div>
      </Container>
    );
  }

  const item = flatItems[idx] || flatItems[0];

  return (
    <Container padding={padding} margin={margin}>
      <MediaWrapper>
        {item.mediaType === "image" ? (
          <StyledImage src={item.url} alt="campaign" onError={advance} />
        ) : (
          // key includes tick so React always creates a fresh <video> element
          // on every advance — autoPlay re-fires even for the same URL.
          <StyledVideo
            key={`${item.url}-${tick}`}
            autoPlay
            muted
            playsInline
            onEnded={advance}
            onError={advance}
          >
            <source src={item.url} />
          </StyledVideo>
        )}
      </MediaWrapper>
    </Container>
  );
});

CampaignCarousel.displayName = "CampaignCarousel";

CampaignCarousel.propTypes = {
  campaigns: PropTypes.array.isRequired,
  padding: PropTypes.string,
  margin: PropTypes.string,
  scheduleViewsMap: PropTypes.object,
};

export default CampaignCarousel;
